import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Pagination,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Paper,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAirportsRequest,
  deleteAirportRequest,
  resetAirportState,
  fetchAllAirportRequest,
} from '../../redux/Airports/actions/airportActions';
import SearchAndButtonTab from '../../component/ui/SearchAndButtonTab';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ADD_AIRPORT,
  EDIT_AIRPORT,
  VIEW_AIRPORT,
} from '../../component/common/Routes/AppRoutes';
import { formatDateWithAmPm, getStatusClass } from '../../Style';
import '../../styles/Service.scss';
import '../../styles/App.scss';
import Loading from '../../component/ui/Loading';
import nodata from '../../assets/nodata.jpg';
import CustomButton from '../../component/ui/FormFields/Button';
import PerfectScrollbar from 'react-perfect-scrollbar';
import axiosInstance from '../../redux/instance/AxiosInstance';

const AirportList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
 const location = useLocation();
  const tab = location.state?.tab ?? 0;
  const pageNumber= location.state?.page ?? 1;
  const searchNavigateQuery = location.state?.searchQuery ?? "";

  const [searchQuery, setSearchQuery] = useState(searchNavigateQuery ?? "");
  const { airportsList, airport } = useSelector((state) => state.airport || {});
  const user = useSelector((state) => state.auth.user);
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === 'string'
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const {
    data = [],
    loading = false,
    error = null,
    totalRecords = 0,
  } = airportsList;

  
  const [searchColumn, setSearchColumn] = useState('');
  const [page, setPage] = useState(pageNumber);
  const rowsPerPage = 10;
  const viewListLoading = airportsList?.loading;
  const deleteLoading = airportsList?.deleteLoading;
  const [open, setOpen] = useState(false);
  const [airportToDelete, setAirportToDelete] = useState(null);
  const [selectedTab, setSelectedTab] = useState(tab);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const searchTimeoutRef = useRef(null);
  const isInitialLoad = useRef(true);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const searchTimerRef = useRef(null);
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const fetchAirportsData = (
    pageNumber = page,
    tabIndex = selectedTab,
    search = searchQuery,

    searchBy = searchColumn
  ) => {
    const approoveStatus = tabIndex === 0 ? 2 : tabIndex === 1 ? 1 : 3;

    const params = {
      approoved_status: approoveStatus,
    };

    if (search) {
      params.pagination_required = true;
      params.pagesize = rowsPerPage;
      params.pageNo = pageNumber;
      params.search =search;

      if (searchBy && searchBy.trim() !== '') {
        params.search_by = searchBy;
      }
    } else {
      params.pagination_required = true;
      params.pagesize = rowsPerPage;
      params.pageNo = pageNumber;
    }

    dispatch(fetchAirportsRequest(params));
  };

  useEffect(() => {
    if (isInitialLoad.current) {
      dispatch(resetAirportState());
      fetchAirportsData(1, 2);
      isInitialLoad.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialLoad.current) {
      fetchAirportsData(page, selectedTab, searchQuery, searchColumn);
    }
  }, [page, selectedTab]);

  useEffect(() => {
    if (shouldFetchData) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        setPage(1);
        fetchAirportsData(1, selectedTab, searchQuery, searchColumn);
      }, 1000);
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [searchQuery, searchColumn, shouldFetchData]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleTabChange = (event, newTab) => {
    setSelectedTab(newTab);
    setPage(1);
  };

  const handleSearch = (query, column) => {
    setSearchQuery(query);

    if (column !== undefined) {
      setSearchColumn(column);
    }
    if (query) {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        setShouldFetchData(
          1,
          query,
          column !== undefined ? column : searchColumn
        );
      }, 1000);
    } else if (query === '' && searchQuery !== '') {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        setShouldFetchData(1, '', column !== undefined ? column : searchColumn);
      }, 1000);
    }
  };

  const handleColumnChange = (column) => {
    setSearchColumn(column);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleExport = () => {
    const approoveStatus = selectedTab === 0 ? 2 : selectedTab === 1 ? 1 : 3;
    const params = {
      approoved_status: approoveStatus,
      pagination_required: false,
    };
    if (searchQuery) {
      params.pagination_required = false;
      params.search = searchQuery;

      if (searchColumn && searchColumn.trim() !== '') {
        params.search_by = searchColumn;
      }
    } else {
      params.pagination_required = false;
    }

    const apiCall = axiosInstance.get(`/airports/list`, { params });

    const escapeCSVField = (field) => {
      if (field == null) return '';
      const value = String(field);
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    apiCall
      .then((response) => {
        const allAirportsData = response?.data?.data || [];

        const csvHeaders = [
          'Airport Name',
          'Airport Short Name',
          'Airport Type',
          'Staff Edit Approval Status',
          'Edit By',
          'Created Date',
          'Status',
          'Approval Status',
        ];

        const csvRows = allAirportsData.map((airport) => [
          escapeCSVField(airport.name),
          escapeCSVField(airport.short_name),
          escapeCSVField(airport.airport_type),
          escapeCSVField(
            airport.staff_edited_status === null
              ? 'No Changes'
              : airport.staff_edited_status === 0
              ? 'Pending'
              : airport.staff_edited_status === 1
              ? 'Approved'
              : 'Rejected'
          ),
          escapeCSVField(
            airport?.airportUpdateLogs?.[0]?.Staff?.full_name || '-'
          ),
          escapeCSVField(formatDateWithAmPm(airport?.created_at) || '-'),
          escapeCSVField(airport.status === 1 ? 'Active' : 'Inactive'),
          escapeCSVField(
            airport?.approoved_status === 1
              ? 'Approved'
              : airport?.approoved_status === 2
              ? 'Pending'
              : 'Rejected'
          ),
        ]);

        const csvContent = [csvHeaders, ...csvRows]
          .map((row) => row.join(','))
          .join('\n');

        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        saveAs(blob, 'airports.csv');
      })
      .catch((error) => {
        console.error('Export error:', error);
      });
  };

  const handleOpenPopup = (airport) => {
    setAirportToDelete(airport);
    setOpen(true);
  };

  const handleClosePopup = () => {
    setAirportToDelete(null);
    setOpen(false);
  };

  const handleConfirmDelete = () => {
    if (airportToDelete) {
      dispatch(deleteAirportRequest(airportToDelete.id));
      toast.success('Airport Deleted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetAirportState());
          fetchAirportsData(1, selectedTab);
        },
      });
    }
    handleClosePopup();
  };

  const tableHeaderMapping = {
    'Airport Name': 'name',
    'Short Name': 'short_name',
    Type: 'airport_type',
    'Staff Edit Approval Status': 'staff_edited_status',
    'Edit By': 'editBy',
    'Approval Status': 'approoved_status',
    Status: 'status',
    'Created Date': 'created_at',
  };

  const renderSortIcon = (property) => {
    if (orderBy !== property) {
      return null;
    }
    return order === 'asc' ? (
      <ArrowUpwardIcon fontSize='small' style={{ marginLeft: '5px' }} />
    ) : (
      <ArrowDownwardIcon fontSize='small' style={{ marginLeft: '5px' }} />
    );
  };

  if (viewListLoading) {
    return <Loading text='Loading Airport details...' />;
  }

  const columns = [
    { value: 'name', label: 'Airport Name' },
    { value: 'short_name', label: 'Short Name' },
    { value: 'airport_type', label: 'Airport Type' },
  ];

  // const sortedAirports = [...data].sort((a, b) => {
  //   if (!orderBy) return 0;

  //   let valA, valB;
  //   if (orderBy === 'created_at') {
  //     valA = new Date(a.created_at || 0);
  //     valB = new Date(b.created_at || 0);
  //   } else if (
  //     orderBy === 'status' ||
  //     orderBy === 'approoved_status' ||
  //     orderBy === 'staff_edited_status'
  //   ) {
  //     valA = Number(a[orderBy] || 0);
  //     valB = Number(b[orderBy] || 0);
  //   } else {
  //     valA = (a[orderBy] || '').toString().toLowerCase();
  //     valB = (b[orderBy] || '').toString().toLowerCase();
  //   }

  //   if (orderBy === 'created_at') {
  //     return order === 'asc' ? valA - valB : valB - valA;
  //   } else if (typeof valA === 'number' && typeof valB === 'number') {
  //     return order === 'asc' ? valA - valB : valB - valA;
  //   } else {
  //     if (valA < valB) return order === 'asc' ? -1 : 1;
  //     if (valA > valB) return order === 'asc' ? 1 : -1;
  //     return 0;
  //   }
  // });

  return (
    <Box className='container'>
      <ToastContainer />
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SearchAndButtonTab
                title='Airports'
                columns={columns}
                hideSearchColumnSelector={true}
                searchColumn={searchColumn}
                setSearchColumn={handleColumnChange}
                searchQuery={searchQuery}
                setSearchQuery={handleSearch}
                handleExport={handleExport}
                routeName={ADD_AIRPORT}
                permissions={permissions}
                user={user}
                type='airports'
                searchPlaceholder='Search by name, short name or type'
              />
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{ marginBottom: '30px', gap: 5 }}
              >
                <Tab label='Pending' />
                <Tab label='Approved' />
                <Tab label='Rejected' />
              </Tabs>

              {loading ? (
                <Box textAlign='center' py={5}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    padding: '50px 0',
                  }}
                >
                  <img
                    src={nodata}
                    alt='nodata'
                    style={{ maxWidth: '300px', maxHeight: '300px' }}
                  />
                </div>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <PerfectScrollbar>
                      {data.length > 0 ? (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                onClick={() => handleSort('name')}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  Airport Name
                                  {renderSortIcon('name')}
                                </Box>
                              </TableCell>
                              <TableCell
                                onClick={() => handleSort('short_name')}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  Short Name
                                  {renderSortIcon('short_name')}
                                </Box>
                              </TableCell>
                              <TableCell
                                onClick={() => handleSort('airport_type')}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  Type
                                  {renderSortIcon('airport_type')}
                                </Box>
                              </TableCell>
                              {user?.role === 'Admin' && (
                                <TableCell
                                  onClick={() =>
                                    handleSort('staff_edited_status')
                                  }
                                  style={{
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                  }}
                                >
                                  <Box display='flex' alignItems='center'>
                                    Staff Edit Approval Status
                                    {renderSortIcon('staff_edited_status')}
                                  </Box>
                                </TableCell>
                              )}
                              {user?.role === 'Admin' && (
                                <TableCell>Edit By</TableCell>
                              )}
                              <TableCell
                                onClick={() => handleSort('approoved_status')}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  Approval Status
                                  {renderSortIcon('approoved_status')}
                                </Box>
                              </TableCell>
                              <TableCell
                                onClick={() => handleSort('status')}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  Status
                                  {renderSortIcon('status')}
                                </Box>
                              </TableCell>
                              <TableCell
                                onClick={() => handleSort('created_at')}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  Created Date
                                  {renderSortIcon('created_at')}
                                </Box>
                              </TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.map((airport) => (
                              <TableRow key={airport.id}>
                                <TableCell>{airport.name}</TableCell>
                                <TableCell>{airport.short_name}</TableCell>
                                <TableCell>{airport.airport_type}</TableCell>
                                {user?.role === 'Admin' && (
                                  <TableCell>
                                    <span
                                      className={`badge ${getStatusClass(
                                        airport.staff_edited_status === null
                                          ? 'No Changes'
                                          : airport.staff_edited_status === 0
                                          ? 'Pending'
                                          : airport.staff_edited_status === 1
                                          ? 'Approved'
                                          : 'Rejected'
                                      )}`}
                                    >
                                      {airport.staff_edited_status === null
                                        ? 'No Changes'
                                        : airport.staff_edited_status === 0
                                        ? 'Pending'
                                        : airport.staff_edited_status === 1
                                        ? 'Approved'
                                        : 'Rejected'}
                                    </span>
                                  </TableCell>
                                )}

                                {user?.role === 'Admin' && (
                                  <TableCell>
                                    {airport?.airportUpdateLogs?.[0]?.Staff
                                      ?.full_name || '-'}
                                  </TableCell>
                                )}
                                <TableCell>
                                  <span
                                    className={`badge ${getStatusClass(
                                      airport.approoved_status === 1
                                        ? 'Approved'
                                        : airport.approoved_status === 2
                                        ? 'Pending'
                                        : 'Rejected'
                                    )}`}
                                  >
                                    {airport.approoved_status === 1
                                      ? 'Approved'
                                      : airport.approoved_status === 2
                                      ? 'Pending'
                                      : 'Rejected'}
                                  </span>
                                </TableCell>

                                <TableCell>
                                  <span
                                    className={`badge ${getStatusClass(
                                      airport.status === 1
                                        ? 'Active'
                                        : 'Inactive'
                                    )}`}
                                  >
                                    {airport.status === 1
                                      ? 'Active'
                                      : 'Inactive'}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {formatDateWithAmPm(airport.created_at)}
                                </TableCell>

                                <TableCell>
                                  <Box className='button-list'>
                                    {(permissions?.airports?.view ||
                                      user?.user_type === 1) && (
                                      <Button
                                        variant='outlined'
                                        size='small'
                                        className='view-user-button'
                                        onClick={() =>
                                          navigate(
                                            `${VIEW_AIRPORT}/${airport.id}`,{state:{tab:selectedTab,page:page,searchQuery:searchQuery}}
                                          )
                                        }
                                      >
                                        View
                                      </Button>
                                    )}
                                    {(user?.user_type === 1 ||
                                      permissions?.airports?.edit) && (
                                      <Button
                                        variant='outlined'
                                        className='edit-user-button'
                                        size='small'
                                        onClick={() => {
                                          dispatch(resetAirportState());
                                          navigate(
                                            `${EDIT_AIRPORT}/${airport.id}`,{state:{tabEdit:selectedTab,pageEdit:page,searchQuery:searchQuery}}
                                          );
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                    {(permissions?.airports?.delete ||
                                      user?.user_type === 1) && (
                                      <CustomButton
                                        variant='contained'
                                        className='delete-user-button'
                                        type='button'
                                        size='small'
                                        text={'DELETE'}
                                        onClick={() => handleOpenPopup(airport)}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            padding: '50px 0',
                          }}
                        >
                          <img
                            src={nodata}
                            alt='nodata'
                            style={{ maxWidth: '300px', maxHeight: '300px' }}
                          />
                        </div>
                      )}
                    </PerfectScrollbar>
                  </TableContainer>
                  {totalRecords > 0 && (
                    <Box className='button-box'>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color='primary'
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog open={open} onClose={handleClosePopup}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>{airportToDelete?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size='small'
            onClick={handleClosePopup}
            className='grey-button'
          >
            Cancel
          </Button>
          <CustomButton
            variant='contained'
            className='orange-button'
            type='button'
            size='small'
            loading={deleteLoading}
            text={'DELETE'}
            onClick={() => handleConfirmDelete()}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AirportList;
